import { useEffect } from 'react';

import { Stack, Typography } from '@mui/material';
import type { TextFieldProps } from '@mui/material/TextField';
import { TimePicker } from '@mui/x-date-pickers-pro';
import type { DateRange } from '@mui/x-date-pickers-pro/models';
import type { Dayjs } from 'dayjs';

interface TimeRangeProps {
  startTimeLabel: string;
  endTimeLabel: string;
  inputSize: TextFieldProps['size'];
  inputVariant: TextFieldProps['variant'];
  value?: DateRange<Dayjs>;
  disabled?: boolean;
  readOnly?: boolean;
  onChange: (value: DateRange<Dayjs>) => void;
  onError?: (hasError: boolean) => void;
}

export const TimeRange = ({
  startTimeLabel,
  endTimeLabel,
  inputSize,
  inputVariant,
  value,
  disabled,
  readOnly,
  onChange,
  onError,
}: TimeRangeProps) => {
  const handleOnchange = (data: Dayjs | null, postion: 'Start' | 'End') => {
    if (value) {
      postion === 'Start' ? onChange([data, value[1]]) : onChange([value[0], data]);
    }
  };

  const hasInvalidTimeRange = Boolean(value?.[0] && value[1] && value[0].isAfter(value[1]));

  useEffect(() => {
    if (onError) {
      onError(hasInvalidTimeRange);
    }
  }, [hasInvalidTimeRange, onError]);

  const sharedProps = {
    disabled: disabled,
    readOnly: readOnly,
  };

  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
      <TimePicker
        label={startTimeLabel}
        value={value ? value[0] : null}
        onChange={(value) => handleOnchange(value, 'Start')}
        slotProps={{
          textField: {
            size: inputSize,
            variant: inputVariant,
            fullWidth: true,
            error: hasInvalidTimeRange,
            helperText: hasInvalidTimeRange ? 'Start time must be before end time' : undefined,
          },
        }}
        {...sharedProps}
      />
      <Typography variant="body1" component="span">
        -
      </Typography>
      <TimePicker
        label={endTimeLabel}
        value={value ? value[1] : null}
        onChange={(value) => handleOnchange(value, 'End')}
        slotProps={{
          textField: {
            size: inputSize,
            variant: inputVariant,
            fullWidth: true,
            error: hasInvalidTimeRange,
            helperText: hasInvalidTimeRange ? 'End time must be after start time' : undefined,
          },
        }}
        {...sharedProps}
      />
    </Stack>
  );
};
